import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import omit from 'lodash/omit';
import { AllowedSidebarMap } from '../../hooks';

interface SidebarState {
  isSidebarOpen: boolean;
  sidebarType?: string;
  payload?: unknown;
  allowedSidebars?: AllowedSidebarMap;
  sidebarOptions?: SidebarOptions;
}

export interface SidebarOptions {
  isOverlay?: boolean;
  sidebarWidth?: string | number;
  shouldCloseOnBlanketClick?: boolean;
  className?: string;
  sidebarTitle?: string | JSX.Element;
  shouldAddPaddingAfterTitle?: boolean;
  sideBarDistanceFromTop?: string;
}

export const initialState: SidebarState = {
  isSidebarOpen: false,
};

export interface OpenSidebarPayload {
  sidebarType: string;
  payload?: unknown;
  sidebarOptions?: SidebarOptions;
}

const sidebar = createSlice({
  name: 'app/sidebar',
  initialState,
  reducers: {
    openSidebar(state, action: PayloadAction<OpenSidebarPayload>) {
      state.isSidebarOpen = true;
      state.sidebarType = action.payload.sidebarType;
      state.payload = action.payload.payload;
      state.sidebarOptions = action.payload.sidebarOptions;
    },
    closeSidebar(state) {
      state.isSidebarOpen = false;
      state.sidebarType = undefined;
      state.payload = undefined;
      state.sidebarOptions = undefined;
    },
    allowlistSidebars(state, action: PayloadAction<AllowedSidebarMap | undefined>) {
      state.allowedSidebars = action.payload;
    },
    /**
     * This is used to club both existing sidebars and current sidebars
     * e.g
     *  1. This scenario helps when we call this action two times on single page load
     */
    addToAllowlistSidebars(state, action: PayloadAction<AllowedSidebarMap | undefined>) {
      const currentSidebarList = state.allowedSidebars || {};
      state.allowedSidebars = { ...currentSidebarList, ...action.payload };
    },
    /**
     * This is used to remove specific page sidebars when unmounting that page and keeping common sidebars in stores.
     */
    clearSidebars(state, action: PayloadAction<AllowedSidebarMap | undefined>) {
      if (!action.payload) {
        state.allowedSidebars = undefined;
        return;
      }

      const currentSidebarList = state.allowedSidebars || {};
      // Omitting specific sidebars when page is unmounting otherwise sidebars never gets clear
      const updatedSidebars = omit(currentSidebarList, Object.keys(action.payload));
      state.allowedSidebars = updatedSidebars;
    },
    updateSidebarOptions(state, action: PayloadAction<SidebarOptions | undefined>) {
      state.sidebarOptions = action.payload;
    },
  },
});

export const actions = sidebar.actions;

export const {
  allowlistSidebars,
  addToAllowlistSidebars,
  closeSidebar,
  openSidebar,
  updateSidebarOptions,
  clearSidebars,
} = sidebar.actions;

export default sidebar.reducer;

export const sidebarState = (state: any) => state?.sidebar;
